import React, { useState, useEffect, Fragment } from 'react';
import parse from 'html-react-parser';
import Input from '../../1_Atoms/Forms/Input/Input';
import Button from '../../1_Atoms/Button/Button';
import FormGroup from '../../2_Molecules/FormGroup/FormGroup';
import { handleRequest } from '../../../utils/httpRequest';
import { getEndpointUrl } from '../../../utils/endPoints';
import Form from '../../2_Molecules/Form/Form';
import { languageController } from '../../../controllers/language/languageController';
import pidController from '../../../controllers/pidController';
import { useForm } from '../../../hooks/useForm';
import { Svg } from '../../1_Atoms/Svg/Svg';
import Select from '../../1_Atoms/Forms/Select/Select';
import FormContainer from '../../2_Molecules/FormContainer/FormContainer';
import { convertLangCodeToSingleLetter } from '../../../utils/helpers';
import FormConfirmation from '../../2_Molecules/FormConfirmation/FormConfirmation';

function KMI({ formExtraParams }) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [modelOptions, setModelOptions] = useState([]);
  const [formError, setFormError] = useState(null)

  const { language, year, model, source, formHeading, formImage, formDisclaimer, formSubmitButtonText } = formExtraParams

  // True if model name and year are provided from parent hidden inputs.
  const hasModelData = (model && year)

  const initState = {
    firstname: '',
    lastname: '',
    email: '',
    postalcode: '',
    modelData: hasModelData ? [year, model].join(' ') : '',
  }

  const validations = {
    firstname: ['required', 'name', 'nameMaxMin'],
    lastname: ['required', 'name', 'nameMaxMin'],
    email: ['required', 'email'],
    postalcode: ['required', 'postalcode'],
    modelData: ['required'],
  }

  const { handleSubmit, handleChange, data, errors, setData } = useForm({
    initState,
    validations,
    // Send the form data if validated
    onSubmit: async () => {
      try {
        const endPointUrl = getEndpointUrl('kmi');
        const modelDataArr = data.modelData.split(" ");
        // Other data to be sent
        const otherData = {
          language: convertLangCodeToSingleLetter(language),
          pid: pidController.getPid('kmi'),
          source: source,
          year: modelDataArr[0],
          model: modelDataArr[1],
        }
        // Remove modelData as no longer needed
        const { modelData, ...restData } = data

        const res = await handleRequest('post', endPointUrl, { ...restData, ...otherData });

        if (res.Success) {
          setIsSubmitted(true);
          setData(initState)
        } else {
          setFormError(res.state ? res.state : res.Errors[0])
        }
      } catch (err) {
        console.log(err)
      }
    },
  })

  useEffect(() => {
    async function fetchMazdaModels() {
      try {
        const res = await handleRequest('get', getEndpointUrl('model-years'));

        if (res.status === 200 && res.data) {
          const modelList = res.data['model_years'];

          if (!modelList) return;

          const mappedList = modelList.map(modelData => {
            const yearAndName = [modelData.year?.title, modelData.carline?.title].join(' ')
            return ({
              value: yearAndName,
              label: yearAndName
            })
          })

          setModelOptions(mappedList)
        }
      } catch (err) {
        console.log(err)
      }
    }

    // If "model" data (year + model name) is not provided from the parent inputs
    // get the model list from the api.
    if (!hasModelData) {
      fetchMazdaModels()
    }
  }, [])

  const { firstname, lastname, email, postalcode, modelData } = data;

  return (
    <div className="mz-kmi" data-mz-form-type="KMI" style={{ backgroundImage: formImage ? `url(${formImage})` : '' }}>
      <div className="mz-kmi__inner">
        <div className="mz-container">
          <div className="mz-full-width grid-inner-2-10-md grid-inner-4-6-lg">
            <FormContainer
              isSubmitted={isSubmitted}
              confirmation={(
                <FormConfirmation 
                  heading={languageController.getCopy('kmi', 'confirmationHeading')}
                  subHeading={languageController.getCopy('kmi', 'confirmationSubHeading')}
                  buttonLabel={languageController.getCopy('kmi', 'confirmationSubmit')}
                  onClick={() => setIsSubmitted(false)}
                  isDark={true}
                />
              )}
              heading={formHeading ? parse(formHeading) : ''}
              disclaimer={formDisclaimer ? parse(formDisclaimer) : ''}
            >
              <Form
                type="kmi"
                onSubmit={handleSubmit}
                isDark={true}
                noValidate={true}
                errorMessage={formError}
              >
                {!hasModelData && (
                  <Select
                    name="modelData"
                    label={languageController.getFormCopy('label', 'model')}
                    value={modelData}
                    isRequired={true}
                    errorMessage={errors.modelData}
                    onChange={handleChange}
                    placeholder={languageController.getFormCopy('placeholder', 'model')}
                    options={modelOptions}
                  />
                )}
                <FormGroup md={2}>
                  <Input
                    type="text"
                    name="firstname"
                    label={languageController.getFormCopy('label', 'firstname')}
                    value={firstname}
                    isRequired={true}
                    errorMessage={errors.firstname}
                    onChange={handleChange}
                    maxLength={50}
                  />
                  <Input
                    type="text"
                    name="lastname"
                    label={languageController.getFormCopy('label', 'lastname')}
                    value={lastname}
                    isRequired={true}
                    errorMessage={errors.lastname}
                    onChange={handleChange}
                    maxLength={50}
                  />
                </FormGroup>
                <Input
                  type="email"
                  name="email"
                  label={languageController.getFormCopy('label', 'email')}
                  value={email}
                  isRequired={true}
                  errorMessage={errors.email}
                  onChange={handleChange}
                  maxLength={320}
                />
                <FormGroup md={2}>
                  <Input
                    type="text"
                    name="postalcode"
                    label={languageController.getFormCopy('label', 'postalcode')}
                    value={postalcode}
                    isRequired={true}
                    errorMessage={errors.postalcode}
                    onChange={handleChange}
                    maxLength={7}
                  />
                </FormGroup>
                <Button
                  type="submit"
                  className="mz-btn--primary mz-btn--red"
                  label={formSubmitButtonText ? formSubmitButtonText : languageController.getCopy('kmi', 'submit')}
                />
              </Form>
            </FormContainer>
          </div>
        </div>
      </div>
    </div>
  )
}

export default KMI
